'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Sparkles, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

import { useEnhancePageMutation } from '../services/qr-review.mutations';

// ─── Types ────────────────────────────────────────────────────────────────────

interface EnhancementPanelProps {
  batchId: number;
  pageId: number;
  imageSrc: string | null;
  onClose: () => void;
}

type ColorMode = 'color' | 'grayscale' | 'document';

// ─── Sharpen helper ───────────────────────────────────────────────────────────

function applySharpen(imageData: ImageData): ImageData {
  const kernel = [0, -1, 0, -1, 5, -1, 0, -1, 0];
  const data = imageData.data;
  const width = imageData.width;
  const height = imageData.height;
  const result = new ImageData(new Uint8ClampedArray(data), width, height);

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      for (let c = 0; c < 3; c++) {
        let sum = 0;
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const idx = ((y + ky) * width + (x + kx)) * 4 + c;
            sum += data[idx] * kernel[(ky + 1) * 3 + (kx + 1)];
          }
        }
        result.data[(y * width + x) * 4 + c] = Math.min(255, Math.max(0, sum));
      }
    }
  }
  return result;
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function EnhancementPanel({
  batchId,
  pageId,
  imageSrc,
  onClose
}: EnhancementPanelProps) {
  const t = useTranslations('qrReview');
  const enhanceMutation = useEnhancePageMutation();

  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [grayscale, setGrayscale] = useState(0);
  const [colorMode, setColorMode] = useState<ColorMode>('color');
  const [sharpen, setSharpen] = useState(false);

  // ── Color mode selection ─────────────────────────────────────────────────
  function handleColorMode(mode: ColorMode) {
    setColorMode(mode);
    if (mode === 'color') {
      setGrayscale(0);
    } else if (mode === 'grayscale') {
      setGrayscale(100);
    } else {
      // document: high contrast B&W
      setGrayscale(100);
      setContrast(150);
    }
  }

  // ── Auto enhance ─────────────────────────────────────────────────────────
  function handleAutoEnhance() {
    setBrightness(115);
    setContrast(140);
    setGrayscale(100);
    setColorMode('document');
  }

  // ── Apply ─────────────────────────────────────────────────────────────────
  async function handleApply() {
    if (!imageSrc) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageSrc;

    await new Promise<void>((resolve) => {
      img.onload = () => resolve();
    });

    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;

    ctx.filter = `brightness(${brightness}%) contrast(${contrast}%) grayscale(${grayscale}%)`;
    ctx.drawImage(img, 0, 0);
    ctx.filter = 'none';

    if (sharpen) {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const sharpened = applySharpen(imageData);
      ctx.putImageData(sharpened, 0, 0);
    }

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        enhanceMutation.mutate(
          { batchId, pageId, imageBlob: blob },
          { onSuccess: () => onClose() }
        );
      },
      'image/jpeg',
      0.92
    );
  }

  const cssFilter = `brightness(${brightness}%) contrast(${contrast}%) grayscale(${grayscale}%)`;

  return (
    /* Full overlay */
    <div className='absolute inset-0 z-10 flex flex-col bg-black/50'>
      {/* Content card */}
      <div className='absolute inset-4 flex flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-900'>
        {/* ── Header ── */}
        <div className='flex items-center justify-between border-b px-5 py-3'>
          <h2 className='text-sm font-semibold text-slate-800 dark:text-slate-100'>
            {t('enhanceTitle')}
          </h2>
          <button
            type='button'
            onClick={onClose}
            className='flex h-7 w-7 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800'
          >
            <X className='h-4 w-4' />
          </button>
        </div>

        {/* ── Body: preview + controls ── */}
        <div className='flex min-h-0 flex-1 overflow-hidden'>
          {/* ── LEFT: Preview ── */}
          <div className='flex flex-1 items-center justify-center overflow-hidden bg-slate-100 p-4 dark:bg-slate-950'>
            {imageSrc ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={imageSrc}
                alt='Preview'
                style={{ filter: cssFilter }}
                className='h-full w-full object-contain'
              />
            ) : (
              <div className='flex h-40 w-40 items-center justify-center rounded-xl border-2 border-dashed border-slate-300 text-slate-400 dark:border-slate-700'>
                <span className='text-sm'>—</span>
              </div>
            )}
          </div>

          {/* ── RIGHT: Controls ── */}
          <div className='flex w-72 flex-shrink-0 flex-col gap-5 overflow-y-auto border-s bg-white p-5 dark:bg-slate-900'>
            {/* Auto Enhance */}
            <Button
              variant='outline'
              size='sm'
              onClick={handleAutoEnhance}
              className='w-full gap-2'
            >
              <Sparkles className='h-4 w-4' />
              {t('enhanceAutoEnhance')}
            </Button>

            {/* Brightness */}
            <div className='flex flex-col gap-2'>
              <div className='flex items-center justify-between'>
                <label className='text-xs font-medium text-slate-600 dark:text-slate-300'>
                  {t('enhanceBrightness')}
                </label>
                <span className='text-xs text-slate-400 tabular-nums'>
                  {brightness}%
                </span>
              </div>
              <input
                type='range'
                min={50}
                max={200}
                step={5}
                value={brightness}
                onChange={(e) => setBrightness(Number(e.target.value))}
                className='w-full accent-blue-500'
              />
            </div>

            {/* Contrast */}
            <div className='flex flex-col gap-2'>
              <div className='flex items-center justify-between'>
                <label className='text-xs font-medium text-slate-600 dark:text-slate-300'>
                  {t('enhanceContrast')}
                </label>
                <span className='text-xs text-slate-400 tabular-nums'>
                  {contrast}%
                </span>
              </div>
              <input
                type='range'
                min={50}
                max={200}
                step={5}
                value={contrast}
                onChange={(e) => setContrast(Number(e.target.value))}
                className='w-full accent-blue-500'
              />
            </div>

            {/* Color Mode */}
            <div className='flex flex-col gap-2'>
              <label className='text-xs font-medium text-slate-600 dark:text-slate-300'>
                {t('enhanceGrayscale')}
              </label>
              <div className='flex overflow-hidden rounded-md border border-slate-200 dark:border-slate-700'>
                {(
                  [
                    { mode: 'color', label: t('enhanceModeColor') },
                    { mode: 'grayscale', label: t('enhanceModeGrayscale') },
                    { mode: 'document', label: t('enhanceModeDocument') }
                  ] as { mode: ColorMode; label: string }[]
                ).map(({ mode, label }, idx) => (
                  <button
                    key={mode}
                    type='button'
                    onClick={() => handleColorMode(mode)}
                    className={cn(
                      'flex-1 py-1.5 text-[11px] font-medium transition-colors',
                      idx !== 0 &&
                        'border-s border-slate-200 dark:border-slate-700',
                      colorMode === mode
                        ? 'bg-blue-500 text-white'
                        : 'bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800'
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Sharpen */}
            <div className='flex items-center gap-2.5'>
              <Checkbox
                id='enhance-sharpen'
                checked={sharpen}
                onCheckedChange={(checked) => setSharpen(Boolean(checked))}
              />
              <label
                htmlFor='enhance-sharpen'
                className='cursor-pointer text-xs font-medium text-slate-600 dark:text-slate-300'
              >
                {t('enhanceSharpen')}
              </label>
            </div>

            {/* Spacer */}
            <div className='flex-1' />

            {/* Action buttons */}
            <div className='flex flex-col gap-2'>
              <Button
                onClick={handleApply}
                disabled={!imageSrc || enhanceMutation.isPending}
                className='w-full'
              >
                {enhanceMutation.isPending ? '…' : t('enhanceApply')}
              </Button>
              <Button
                variant='outline'
                onClick={onClose}
                disabled={enhanceMutation.isPending}
                className='w-full'
              >
                {t('enhanceCancel')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
